import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { STORE_LABELS, capitalize } from "../constants/trykkeguide";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function TrykkeguidePrint() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const selectedGrener = (searchParams.get("gren") || "").split(",").filter(Boolean);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchClub() {
      try {
        const r = await fetch(`${API_BASE_URL}/trykkeguide/${id}`);
        if (!r.ok) throw new Error();
        setClub(await r.json());
      } catch {
        console.error("Kunne ikke hente klubb");
      } finally {
        setLoading(false);
      }
    }
    fetchClub();
  }, [id]);

  if (loading) return <div className="text-center py-10 text-gray-500">Laster inn trykkeguide…</div>;
  if (!club) return <div className="text-center py-10 text-gray-400">Fant ikke klubben.</div>;

  const garments = [...club.garments]
    .filter((g) => selectedGrener.length === 0 || (g.grener || []).some((gg) => selectedGrener.includes(gg)))
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  return (
    <div className="min-h-screen bg-gray-100 py-8 print:bg-white print:py-0">
      <style>
        {`
@media print {
  @page { size: A4; margin: 15mm; }
  html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
.garment-block { break-inside: avoid; page-break-inside: avoid; }
`}
      </style>

      <div className="bg-white w-[210mm] max-w-full mx-auto p-10 print:p-0 print:w-auto print:shadow-none shadow-lg">
        <div className="flex items-center gap-3 border-b border-gray-200 pb-2 mb-4">
          {club.logo?.url && (
            <img src={club.logo.url} alt={club.name} className="w-10 h-10 object-contain" />
          )}
          <div>
            <h1 className="text-lg font-bold text-gray-900">{club.name}</h1>
            <p className="text-xs text-gray-500">
              Trykkeguide{STORE_LABELS[club.butikktilhorighet] ? ` — Sport 1 ${STORE_LABELS[club.butikktilhorighet]}` : ""}
              {selectedGrener.length > 0 ? ` — ${selectedGrener.map(capitalize).join(", ")}` : " — Alle grener"}
            </p>
          </div>
        </div>

        {garments.length === 0 ? (
          <p className="text-gray-400">Ingen plagg {selectedGrener.length > 0 ? `for ${selectedGrener.map(capitalize).join(", ")} ` : ""}i guiden ennå.</p>
        ) : (
          <div className="flex flex-col gap-8">
            {garments.map((garment) => (
              <div key={garment._id} className="garment-block">
                <div className="flex items-center flex-wrap gap-2 mb-1">
                  <h2 className="text-lg font-semibold text-gray-900">{garment.name}</h2>
                  {garment.grener?.map((gren) => (
                    <span key={gren} className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-700">
                      {capitalize(gren)}
                    </span>
                  ))}
                </div>
                {(garment.merke || garment.modell) && (
                  <p className="text-xs text-gray-500 mb-2">
                    {[garment.merke, garment.modell].filter(Boolean).join(" — ")}
                  </p>
                )}
                {garment.images.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-4 mb-2">
                    {garment.images.map((img) => (
                      <img
                        key={img._id}
                        src={img.url}
                        alt={img.caption || garment.name}
                        className="max-h-[90mm] max-w-full object-contain border border-gray-100"
                      />
                    ))}
                  </div>
                )}
                {garment.instructions && (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{garment.instructions}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TrykkeguidePrint;
